import { useParams, Navigate, Link } from "react-router-dom";
import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";
import SEO from "@/components/marketing/SEO";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { blogPosts, defaultAuthor } from "@/data/blogPosts";
import { ArrowLeft, ArrowRight } from "lucide-react";

const BlogArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const author = post.author || defaultAuthor;

  const relatedPosts = blogPosts
    .filter((p) => p.category === post.category && p.slug !== post.slug)
    .slice(0, 3);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.dateISO,
    dateModified: post.dateISO,
    author: {
      "@type": author.url ? "Person" : "Organization",
      name: author.name,
      ...(author.url && { url: author.url }),
    },
    publisher: {
      "@type": "Organization",
      name: "Piktable",
      url: "https://piktable.ch",
      logo: {
        "@type": "ImageObject",
        url: "https://piktable.ch/og-image.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://piktable.ch/blog/${post.slug}`,
    },
    keywords: post.keywords,
    inLanguage: "fr-CH",
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={post.title}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        ogType="article"
        structuredData={structuredData}
      />

      <Header />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-8">
          {/* Back link */}
          <div className="max-w-3xl mx-auto mb-8">
            <Link
              to="/blog"
              className="text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au blog
            </Link>
          </div>

          {/* Article header */}
          <article className="max-w-3xl mx-auto">
            <header className="mb-10">
              <Badge variant="secondary" className="mb-4">
                {post.category}
              </Badge>
              <h1 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
                {post.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>{author.name}</span>
                <span>-</span>
                <time dateTime={post.dateISO}>{post.date}</time>
              </div>
            </header>

            {/* Article content */}
            <div className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-playfair prose-headings:text-foreground prose-p:text-muted-foreground prose-a:text-primary prose-strong:text-foreground prose-li:text-muted-foreground">
              {post.content}
            </div>
          </article>

          {/* Related articles */}
          {relatedPosts.length > 0 && (
            <section className="max-w-6xl mx-auto mt-20">
              <h2 className="font-playfair text-2xl font-bold text-foreground mb-8 text-center">
                Articles similaires
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {relatedPosts.map((related) => (
                  <Link key={related.slug} to={`/blog/${related.slug}`}>
                    <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-border">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="secondary" className="text-xs">
                            {related.category}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {related.date}
                          </span>
                        </div>
                        <h3 className="font-playfair text-lg font-bold text-foreground leading-tight">
                          {related.title}
                        </h3>
                      </CardHeader>
                      <CardContent>
                        <p className="text-muted-foreground text-sm line-clamp-3">
                          {related.excerpt}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* CTA */}
          <section className="max-w-2xl mx-auto mt-20 text-center bg-card border border-border rounded-2xl p-10">
            <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-4">
              Prêt à simplifier vos réservations ?
            </h2>
            <p className="text-muted-foreground mb-6">
              Découvrez comment Piktable peut transformer la gestion de votre
              restaurant en Suisse romande.
            </p>
            <div className="flex justify-center">
              <Button asChild size="lg">
                <Link to="/contact">
                  Nous contacter <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogArticle;
